import { useEffect, useState } from 'react';
import { isMobile } from '../../../utils/mobile-utils';
import { getBrowserName, getOperatingSystem } from '../../../utils/app-utils';

export function useKeyboardAwareFooter(footerRef: React.RefObject<HTMLElement>) {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;

    // Early return if not a mobile device
    if (!isMobile()) {
      return;
    }

    // Check if Visual Viewport API is supported

    // Handle viewport resize for modern browsers (iOS Safari + Chrome Android)
    const handleViewportResize = () => {
      try {
        const viewport = window.visualViewport;
        if (!viewport) return;

        const offset = window.innerHeight - viewport.height;
        // Use a more dynamic threshold based on screen size (20% of window height)
        const threshold = Math.max(100, window.innerHeight * 0.2);

        if (offset > threshold) {
          setIsKeyboardOpen(true);
          footer.style.transform = `translateY(-${offset}px)`;
        } else {
          setIsKeyboardOpen(false);
          footer.style.transform = 'translateY(0)';
        }
      } catch (error) {
        console.error('Error handling viewport resize:', error);
      }
    };

    // Fallback: detect focus/blur for older browsers or when Visual Viewport API is not available
    const input = footer.querySelector('textarea');
    const handleFocus = () => {
      // Only set keyboard open if Visual Viewport API is not available
      if (getBrowserName() == 'Safari') setIsKeyboardOpen(true);
    };

    const handleBlur = () => {
      // Only set keyboard closed if Visual Viewport API is not available
      if (getOperatingSystem() == 'Safari') setIsKeyboardOpen(false);
    };

    input?.addEventListener('focus', handleFocus);
    input?.addEventListener('blur', handleBlur);

    // Use Visual Viewport API if available, otherwise rely on focus/blur
    if ('visualViewport' in window && window.visualViewport && getOperatingSystem() !== 'Safari') {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }

    return () => {
      input?.removeEventListener('focus', handleFocus);
      input?.removeEventListener('blur', handleBlur);
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
    };
  }, [footerRef]);

  return isKeyboardOpen;
}
